import React, { useState, useEffect, useMemo, useCallback } from 'react';
import BottomSheet from './BottomSheet';
import UserList, { User as UserListUser } from '../lists/UserList';
import { siteExecutorsApi, SiteExecutor } from '../../../api/siteExecutorsApi';
import config from '../../../config/config';
import './UserSelector.css';

export interface ExecutorSelectorProps {
  value: number | null;
  onChange: (siteExecutorId: number | null) => void;
  siteId: number;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  id?: string;
  name?: string;
}

const ExecutorSelector: React.FC<ExecutorSelectorProps> = ({
  value,
  onChange,
  siteId,
  placeholder = 'Выберите исполнителя',
  label,
  required = false,
  disabled = false,
  error,
  className = '',
  id,
  name
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [executors, setExecutors] = useState<SiteExecutor[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (id: number): string => {
    const colors = [
      '#4A90E2', '#50C878', '#FF6B6B', '#FFA500',
      '#9B59B6', '#E74C3C', '#3498DB', '#2ECC71'
    ];
    return colors[id % colors.length];
  };

  useEffect(() => {
    if (siteId > 0) {
      loadExecutors();
    }
  }, [siteId]);

  const loadExecutors = async () => {
    setLoading(true);
    try {
      const data = await siteExecutorsApi.getSiteExecutors(siteId);
      setExecutors(data);
    } catch (err) {
      console.error('Ошибка загрузки исполнителей:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedExecutor = executors.find(e => e.id === value);

  const handleSelect = useCallback((executorId: number) => {
    onChange(executorId);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange]);

  const filteredExecutors = useMemo(() => {
    if (!searchQuery) return executors;
    const searchLower = searchQuery.toLowerCase();
    return executors.filter(executor => {
      const name = executor.userName || executor.displayName || `Исполнитель ${executor.id}`;
      return name.toLowerCase().includes(searchLower) ||
             executor.id.toString().includes(searchLower);
    });
  }, [executors, searchQuery]);

  const userListUsers: UserListUser[] = useMemo(() => {
    const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
    return filteredExecutors.map(executor => {
      const displayName = executor.userName || executor.displayName || `Исполнитель ${executor.id}`;
      return {
        userId: executor.id.toString(),
        userName: displayName + (executor.isExternal ? ' (внешний)' : ''),
        userRole: executor.isExternal ? 'Внешний исполнитель' : undefined,
        avatarColor: getAvatarColor(executor.id),
        avatarUrl: executor.avatarUrl
          ? (executor.avatarUrl.startsWith('http') ? executor.avatarUrl : `${apiBaseUrl}${executor.avatarUrl}`)
          : undefined,
        onSelectUser: () => handleSelect(executor.id)
      };
    });
  }, [filteredExecutors, handleSelect]);

  const handleToggle = () => {
    if (!disabled) setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleSelectorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const displayName = selectedExecutor
    ? (selectedExecutor.userName || selectedExecutor.displayName || `Исполнитель ${selectedExecutor.id}`) +
      (selectedExecutor.isExternal ? ' (внешний)' : '')
    : null;

  return (
    <div className={`user-selector ${className}`} onClick={handleSelectorClick}>
      {label && (
        <label htmlFor={id} className="user-selector-label">
          {label}
          {required && <span className="required"> *</span>}
        </label>
      )}

      <div
        className={`user-selector-button ${disabled ? 'disabled' : ''} ${error ? 'error' : ''}`}
        onClick={handleToggle}
        id={id}
      >
        <div className="user-selector-content">
          {selectedExecutor ? (
            <div className="user-selected">
              <div className="user-avatar">
                {selectedExecutor.avatarUrl ? (
                  <img
                    src={selectedExecutor.avatarUrl.startsWith('http')
                      ? selectedExecutor.avatarUrl
                      : `${config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '')}${selectedExecutor.avatarUrl}`}
                    alt={displayName || ''}
                    className="user-avatar-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="user-avatar-circle"
                  style={{
                    backgroundColor: getAvatarColor(selectedExecutor.id),
                    display: selectedExecutor.avatarUrl ? 'none' : 'flex'
                  }}
                >
                  {selectedExecutor.isExternal ? (
                    <i className="ri-user-line" style={{ fontSize: '1rem' }} />
                  ) : (
                    getUserInitials(displayName || '')
                  )}
                </div>
              </div>
              <div className="user-info">
                <div className="user-name">{displayName}</div>
                {selectedExecutor.isExternal && (
                  <div className="user-role">Внешний исполнитель</div>
                )}
              </div>
            </div>
          ) : (
            <span className="user-placeholder">{placeholder}</span>
          )}
        </div>
        <div className="user-selector-arrow">
          <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line`}></i>
        </div>
      </div>

      <BottomSheet
        isOpen={isOpen}
        onClose={handleClose}
        title="Выберите исполнителя"
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="user-selector-content">
          <div className="user-search">
            <div className="search-input-wrapper">
              <i className="ri-search-line search-icon"></i>
              <input
                type="text"
                placeholder="Поиск по имени или ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button className="search-clear" onClick={() => setSearchQuery('')}>
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          <div className="user-selector-list-container">
            {loading ? (
              <div className="user-loading">
                <i className="ri-loader-4-line ri-spin"></i>
                <span>Загрузка исполнителей...</span>
              </div>
            ) : filteredExecutors.length === 0 ? (
              <div className="user-empty">
                <i className="ri-search-line"></i>
                <span>Исполнители не найдены</span>
              </div>
            ) : (
              <div className="user-selector-user-list-wrapper">
                <UserList
                  users={userListUsers}
                  onAddUser={undefined}
                  onRemoveUser={undefined}
                  showRemoveButton={false}
                  canManagePermissions={false}
                  canManageMembers={false}
                  className="user-selector-user-list"
                />
              </div>
            )}
          </div>
        </div>
      </BottomSheet>

      {error && (
        <div className="user-error">
          <i className="ri-error-warning-line"></i>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default ExecutorSelector;
